import useCustomToast from "@/hooks/useCustomToast";
import { useColorMode } from "@chakra-ui/color-mode";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiMaximize2,
  FiMinimize2,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { type IHighlight, PdfHighlighter } from "react-pdf-highlighter";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker?url";
import "react-pdf-highlighter/dist/style.css";
import { useDocumentStore, type PdfFileData } from "../../stores/documents";
import type { IconType } from "react-icons/lib";

// PDF.js Worker 설정
GlobalWorkerOptions.workerSrc = workerSrc;

interface SampleDocument {
  id: string;
  name: string;
  icon: IconType;
  template?: string;
}

interface DocumentUploaderProps {
  onTemplateLoad?: (templateName: string) => void;
  onReset?: () => void;
}

const SAMPLE_DOCUMENTS: SampleDocument[] = [
  {
    id: "lab_report",
    name: "실험보고서",
    icon: FiFileText,
    template: "lab_report",
  },
  {
    id: "meeting_minutes",
    name: "회의록",
    icon: FiFileText,
    template: "meeting_minutes",
  },
  {
    id: "result_report",
    name: "결과보고서",
    icon: FiFileText,
    template: "result_report",
  },
];

const DocumentUploader = ({
  onTemplateLoad,
  onReset,
}: DocumentUploaderProps) => {
  const { colorMode } = useColorMode();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const [pdfViewerKey, setPdfViewerKey] = useState(0);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const loadingRef = useRef<boolean>(false);

  const {
    pdfFile,
    pdfDocument,
    currentPage,
    totalPages,
    isDocumentLoaded,
    fileSize,
    setCurrentPage,
    setPdfFile,
    setPdfDocument,
    setTotalPages,
    setIsDocumentLoaded,
    setPdfError,
    setFileSize,
  } = useDocumentStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const highlighterRef = useRef<PdfHighlighter<IHighlight>>(null);
  const scrollToRef = useRef<(highlight: IHighlight) => void>(() => {});

  const hasReloadedRef = useRef<boolean>(false);

  const resetPdfStateOnly = useCallback(() => {
    setPdfFile(null);
    setPdfDocument(null);
    setPdfError(null);
    setIsDocumentLoaded(false);
    setIsViewerReady(false);
    setPdfViewerKey(0);
    setFileSize(0);
    localStorage.removeItem("pdfFile");
    localStorage.removeItem("pdf-document-storage");
  }, [
    setPdfFile,
    setPdfDocument,
    setPdfError,
    setIsDocumentLoaded,
    setFileSize,
  ]);

  const resetPdfState = useCallback(() => {
    resetPdfStateOnly();
    if (onReset) {
      onReset();
    }
  }, [resetPdfStateOnly, onReset]);

  const handleFileSelected = useCallback(
    async (file: File) => {
      if (!file || file.type !== "application/pdf" || loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        resetPdfStateOnly();
        setFileSize(file.size);

        const buffer = await file.arrayBuffer();
        const pdfDoc = await getDocument({ data: buffer }).promise;
        await pdfDoc.getPage(1);

        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          setPdfFile({
            data: base64,
            name: file.name,
          } as PdfFileData);
        };
        reader.readAsDataURL(file);

        setPdfDocument(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setIsDocumentLoaded(true);
      } catch (error) {
        console.error("PDF 로드 실패:", error);
        setPdfError(
          error instanceof Error ? error.message : "Failed to load PDF"
        );
        resetPdfStateOnly();
      } finally {
        loadingRef.current = false;
      }
    },
    [
      resetPdfStateOnly,
      setPdfFile,
      setPdfDocument,
      setTotalPages,
      setPdfError,
      setIsDocumentLoaded,
      setFileSize,
    ]
  );

  const handleLoadTemplate = async (templateName: string) => {
    try {
      loadingRef.current = true;
      resetPdfStateOnly();
      setIsViewerReady(false);

      // 회의록인 경우 meeting_minuts.pdf 파일을 불러옴
      const pdfFileName =
        templateName === "meeting_minutes" ? "meeting_minuts" : templateName;

      const pdfResponse = await fetch(
        `/assets/templates/examples/${pdfFileName}.pdf`
      );
      if (!pdfResponse.ok) {
        throw new Error(`PDF 파일을 찾을 수 없습니다: ${pdfFileName}.pdf`);
      }

      const pdfBlob = await pdfResponse.blob();
      setFileSize(pdfBlob.size);

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(pdfBlob);
      });

      setPdfFile({
        data: base64,
        name: `${templateName}.pdf`,
      } as PdfFileData);

      const buffer = await pdfBlob.arrayBuffer();
      const pdfDoc = await getDocument({ data: buffer }).promise;
      setPdfDocument(pdfDoc);
      setTotalPages(pdfDoc.numPages);
      setIsDocumentLoaded(true);

      try {
        const schemaUrl = `/assets/templates/${templateName}.json`;
        const schemaResponse = await fetch(schemaUrl);

        if (schemaResponse.ok) {
          const schemaData = await schemaResponse.json();
          localStorage.setItem("sampleSchema", JSON.stringify(schemaData));

          showSuccessToast(
            `${templateName} 템플릿과 스키마가 성공적으로 로드되었습니다.`
          );

          if (onTemplateLoad) {
            onTemplateLoad(templateName);
          }
        } else {
          showSuccessToast(
            `${templateName} 템플릿이 성공적으로 로드되었습니다.`
          );

          if (onTemplateLoad) {
            onTemplateLoad(templateName);
          }
        }
      } catch {
        showSuccessToast(`${templateName} 템플릿이 성공적으로 로드되었습니다.`);

        if (onTemplateLoad) {
          onTemplateLoad(templateName);
        }
      }
    } catch (error) {
      console.error("템플릿 로드 실패:", error);

      if (error instanceof Error && error.message.includes("PDF")) {
        showErrorToast(error.message);
      } else {
        showErrorToast("템플릿을 로드하는 중 오류가 발생했습니다.");
      }
      resetPdfStateOnly();
    } finally {
      loadingRef.current = false;
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFileSelected(acceptedFiles[0]);
      }
    },
    [handleFileSelected]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
    disabled: loadingRef.current,
  });

  useEffect(() => {
    if (!pdfDocument || !isDocumentLoaded) {
      setIsViewerReady(false);
      return;
    }

    const initializeViewer = async () => {
      try {
        setIsViewerReady(false);

        await new Promise((resolve) => setTimeout(resolve, 500));

        if (pdfDocument.numPages > 0) {
          const page = await pdfDocument.getPage(1);
          await page.getViewport({ scale: 1.0 });
        }

        if (!containerRef.current) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 300));

        setIsViewerReady(true);
        setPdfViewerKey((prev) => prev + 1);
      } catch (error) {
        console.error("PDF 뷰어 초기화 실패:", error);
        setIsViewerReady(false);
        setPdfError("PDF 뷰어 초기화에 실패했습니다.");
      }
    };

    initializeViewer();
  }, [pdfDocument, isDocumentLoaded, setPdfError]);

  useEffect(() => {
    const reloadPdfFromStoredData = async () => {
      if (pdfDocument || loadingRef.current || !pdfFile) {
        return;
      }

      if (hasReloadedRef.current) {
        return;
      }

      try {
        hasReloadedRef.current = true;
        loadingRef.current = true;
        setIsViewerReady(false);

        let buffer: ArrayBuffer;
        let calculatedSize: number;

        if (typeof pdfFile.data === "string") {
          const base64Data = pdfFile.data.split(",")[1];
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          buffer = bytes.buffer;
          calculatedSize = binaryString.length;
        } else {
          buffer = await pdfFile.data.arrayBuffer();
          calculatedSize = buffer.byteLength;
        }

        setFileSize(calculatedSize);

        const pdfDoc = await getDocument({ data: buffer }).promise;
        await pdfDoc.getPage(1);

        setPdfDocument(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setIsDocumentLoaded(true);
      } catch (error) {
        setPdfError(
          error instanceof Error ? error.message : "Failed to reload PDF"
        );
        setPdfFile(null);
        setFileSize(0);
      } finally {
        loadingRef.current = false;
      }
    };

    reloadPdfFromStoredData();
  }, [
    pdfFile,
    pdfDocument,
    setPdfDocument,
    setTotalPages,
    setIsDocumentLoaded,
    setPdfError,
    setPdfFile,
    setFileSize,
  ]);

  useEffect(() => {
    if (!isDocumentLoaded || !containerRef.current) {
      return;
    }

    const handleScroll = () => {
      requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) return;

        const pages = Array.from(container.querySelectorAll(".page")).filter(
          (page): page is HTMLElement => {
            if (!page || !(page instanceof HTMLElement)) return false;
            return page.offsetParent !== null;
          }
        );

        if (!pages.length) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;
        let maxVisibleRatio = 0;
        let mostVisiblePage = pages[0];

        for (const page of pages) {
          const pageRect = page.getBoundingClientRect();
          const visibleTop = Math.max(pageRect.top, containerTop);
          const visibleBottom = Math.min(
            pageRect.bottom,
            containerTop + containerHeight
          );
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibleRatio = visibleHeight / pageRect.height;

          if (visibleRatio > maxVisibleRatio) {
            maxVisibleRatio = visibleRatio;
            mostVisiblePage = page;
          }
        }

        const pageNumber = pages.indexOf(mostVisiblePage) + 1;
        if (
          pageNumber !== currentPage &&
          pageNumber > 0 &&
          pageNumber <= totalPages
        ) {
          setCurrentPage(pageNumber);
        }
      });
    };

    let timeoutId: NodeJS.Timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    containerRef.current?.addEventListener(
      "scroll",
      debouncedHandleScroll,
      true
    );

    const initTimeout = setTimeout(() => {
      if (containerRef.current) {
        handleScroll();
      }
    }, 1500);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initTimeout);
      containerRef.current?.removeEventListener(
        "scroll",
        debouncedHandleScroll,
        true
      );
    };
  }, [currentPage, totalPages, isDocumentLoaded, setCurrentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      const container = containerRef.current;

      if (container) {
        const pages = Array.from(
          container.getElementsByClassName("page")
        ).filter(
          (page): page is HTMLElement =>
            page instanceof HTMLElement && page.offsetParent !== null
        );

        if (pages.length >= newPage) {
          const targetPage = pages[newPage - 1];
          if (targetPage) {
            setTimeout(() => {
              targetPage.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
              });
            }, 100);
          }
        }
      }
    }
  };

  const renderPdfHighlighter = () => {
    if (pdfFile && !pdfDocument && !isDocumentLoaded) {
      return (
        <Flex justify="center" align="center" h="100%" color="gray.500">
          <Spinner mr={2} />
          <Text>PDF 문서 복원 중...</Text>
        </Flex>
      );
    }

    if (
      loadingRef.current ||
      !pdfDocument ||
      !isDocumentLoaded ||
      !isViewerReady ||
      totalPages === 0
    ) {
      return (
        <Flex justify="center" align="center" h="100%" color="gray.500">
          <Spinner mr={2} />
          <Text>PDF 로딩 중...</Text>
        </Flex>
      );
    }

    if (!containerRef.current) {
      return (
        <Flex justify="center" align="center" h="100%" color="gray.500">
          <Spinner mr={2} />
          <Text>PDF 뷰어 초기화 중...</Text>
        </Flex>
      );
    }

    return (
      <PdfHighlighter
        key={`pdf-highlighter-${pdfViewerKey}`}
        ref={highlighterRef}
        pdfDocument={pdfDocument}
        enableAreaSelection={() => false}
        highlights={[]}
        scrollRef={(scrollTo) => {
          scrollToRef.current = scrollTo;
        }}
        onScrollChange={() => {}}
        highlightTransform={() => <div />}
        onSelectionFinished={() => <div />}
      />
    );
  };

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      height="100%"
    >
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          문서 업로드
        </Text>
      </Box>

      {!pdfFile ? (
        <Box
          {...getRootProps()}
          borderWidth={2}
          borderStyle="dashed"
          borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
          borderRadius="lg"
          flex={1}
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          bg={
            isDragActive
              ? colorMode === "light"
                ? "gray.50"
                : "gray.700"
              : "transparent"
          }
          transition="all 0.2s"
          cursor={loadingRef.current ? "default" : "pointer"}
          minH="400px"
        >
          <input {...getInputProps()} />
          {loadingRef.current ? (
            <Spinner size="xl" />
          ) : (
            <>
              <Icon
                as={FiUpload}
                w={16}
                h={16}
                mb={6}
                color={colorMode === "light" ? "gray.400" : "gray.500"}
              />
              <Text fontWeight="medium" fontSize="lg">
                문서 업로드
              </Text>
              <Text fontSize="sm" mt={2}>
                드래그 앤 드롭 또는 클릭하여 선택
              </Text>
              <VStack mt={8} gap={4} align="stretch" w="full" maxW="sm">
                <Text fontSize="sm" textAlign="center">
                  샘플 템플릿 사용
                </Text>
                <HStack gap={2} justify="center">
                  {SAMPLE_DOCUMENTS.map((doc) => (
                    <Button
                      key={doc.id}
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (doc.template) {
                          handleLoadTemplate(doc.template);
                        } else {
                          showSuccessToast(
                            `${doc.name} 템플릿이 곧 제공될 예정입니다.`
                          );
                        }
                      }}
                      disabled={loadingRef.current}
                    >
                      <Icon as={doc.icon} mr={2} />
                      {doc.name}
                    </Button>
                  ))}
                </HStack>
              </VStack>
            </>
          )}
        </Box>
      ) : (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          borderRadius="lg"
          overflow="hidden"
          height="100%"
        >
          <Flex
            p={4}
            justifyContent="space-between"
            alignItems="center"
            bg="transparent"
            shrink={0}
          >
            <HStack gap={2}>
              <Icon as={FiFileText} />
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {pdfFile.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {(() => {
                  const calculateFileSize = () => {
                    if (fileSize > 0) {
                      return (fileSize / (1024 * 1024)).toFixed(2);
                    }

                    if (pdfFile && typeof pdfFile.data === "string") {
                      try {
                        const base64Data = pdfFile.data.split(",")[1];
                        const binaryString = atob(base64Data);
                        const calculatedSize = binaryString.length;
                        return (calculatedSize / (1024 * 1024)).toFixed(2);
                      } catch {
                        return "알 수 없음";
                      }
                    }

                    return "알 수 없음";
                  };

                  const sizeText = calculateFileSize();
                  return `(${sizeText}${sizeText !== "알 수 없음" ? " MB" : ""})`;
                })()}
              </Text>
            </HStack>

            <Button
              size="sm"
              variant="ghost"
              onClick={resetPdfState}
              disabled={loadingRef.current}
            >
              <Icon as={FiX} />
            </Button>
          </Flex>

          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            position="relative"
            borderWidth={1}
            borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
            borderRadius="md"
            height="100%"
            minHeight={0}
          >
            <Flex
              py={2}
              px={4}
              borderBottomWidth={1}
              borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
              justifyContent="space-between"
              alignItems="center"
              shrink={0}
            >
              <HStack gap={4} flex={1} justify="center">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loadingRef.current}
                  size="sm"
                  variant="outline"
                >
                  <Icon as={FiChevronLeft} />
                </Button>
                <Text>
                  {currentPage} / {totalPages}
                </Text>
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loadingRef.current}
                  size="sm"
                  variant="outline"
                >
                  <Icon as={FiChevronRight} />
                </Button>
              </HStack>

              <ButtonGroup size="sm" variant="outline">
                <Button
                  onClick={() => {
                    if (highlighterRef.current?.viewer) {
                      highlighterRef.current.viewer.currentScaleValue =
                        "page-fit";
                    }
                  }}
                  disabled={loadingRef.current}
                >
                  <Icon as={FiMaximize2} />
                </Button>
                <Button
                  onClick={() => {
                    if (highlighterRef.current?.viewer) {
                      highlighterRef.current.viewer.currentScaleValue =
                        "page-width";
                    }
                  }}
                  disabled={loadingRef.current}
                >
                  <Icon as={FiMinimize2} />
                </Button>
              </ButtonGroup>
            </Flex>

            <Box flex={1} position="relative" overflow="hidden">
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="auto"
                ref={containerRef}
                className="pdf-container"
                css={{
                  scrollBehavior: "smooth",
                  "& > div": {
                    position: "relative",
                    height: "100%",
                    width: "100%",
                  },
                  ".PdfHighlighter": {
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    "& > div": {
                      position: "relative",
                      backgroundColor: "white",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  },
                  ".page": {
                    margin: "0 auto",
                    position: "relative",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    width: "100%",
                    maxWidth: "800px",
                    "& > canvas": {
                      width: "100% !important",
                      height: "auto !important",
                      display: "block",
                    },
                  },
                  ".textLayer": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.2,
                    mixBlendMode: "multiply",
                    width: "100%",
                    pointerEvents: "none",
                  },
                  ".annotationLayer": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    pointerEvents: "none",
                  },
                }}
              >
                {renderPdfHighlighter()}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DocumentUploader;